import { PrimeMember } from 'iam/MemberStore'
import Member from 'iam/models/Member'
import { collection } from 'infra/collection'
import { Model } from 'infra/model'
import once from 'lodash/once'
import { VideoCallEvent } from 'messaging/calling/video-call-events'
import { VideoCallParticipant } from 'messaging/calling/VideoCallParticipant'
import {
  VideoCallDto,
  VideoCallParticipantDto,
  VideoCallStatusType,
} from 'messaging/dtos/video-calls'
import Conversation from 'messaging/models/Conversation'
import { action, computed, observable, makeObservable } from 'mobx'
import { EMPTY } from 'rxjs'
import { Observable } from 'rxjs/internal/Observable'
import { filter } from 'rxjs/operators'
import { Id } from 'types/definitions'
import { DateZero, maybeParseDate } from 'utils/date-util'
import { toStream } from 'utils/mobx-util'

/**
 * Call.
 */
export class VideoCall extends Model<VideoCallDto, VideoCall> {
  id = Id.none

  @observable callStatus = VideoCallStatus.Unknown
  @observable dateCreated = DateZero()
  @observable dateCompleted: Date | null = null

  /**
   * None instance.
   */
  static get none() {
    return lazyNone()
  }

  /**
   * Call participants.
   */
  participants = collection<VideoCallParticipantDto, VideoCallParticipant>({
    create: () => new VideoCallParticipant(this, this.primeMember),
    getDataId: (d) => d.user_id,
    getModelId: (d) => d.userId,
  })

  /**
   * Events for this call.
   */
  events$: Observable<VideoCallEvent>

  /**
   * Emits when the call is completed.
   * Does not emit immediately.
   */
  completed$: Observable<unknown>

  constructor(
    public conversation: Conversation,
    private primeMember: PrimeMember,
    private getTime: () => Date,
    events$: Observable<VideoCallEvent>
  ) {
    super()
    makeObservable(this)
    this.events$ = events$.pipe(filter((e) => e.call === this))
    this.completed$ = toStream(
      () => this.callStatus === VideoCallStatus.Completed
    ).pipe(filter(Boolean))
  }

  /**
   * Participants that are not the current user.
   */
  @computed
  get otherParticipants(): readonly VideoCallParticipant[] {
    return this.participants.filter((p) => p.member.isCurrentUser === false)
  }

  /**
   * Gets the current user participant if possible.
   */
  @computed
  get currentUserParticipant() {
    return this.participants.find((p) => p.member.isCurrentUser) ?? null
  }

  /**
   * Parse attributes from the DTO.
   *
   * @param attrs
   */
  parse(attrs: Partial<VideoCallDto>): Partial<VideoCall> {
    this.participants.reset(attrs.participants)
    return {
      id: attrs.id,
      dateCreated: maybeParseDate(attrs.date_created),
      dateCompleted: maybeParseDate(attrs.date_completed),
      callStatus: VideoCall.mapCallStatus(attrs.call_status),
    }
  }

  /**
   * Participant is added to the call. Idempotent.
   *
   * @param participantUserId
   */
  ensureParticipant(participantUserId: string) {
    return this.participants.set({ user_id: participantUserId })
  }

  /**
   * Used to optimistically mark the call as completed. Called when the
   * current user is declining or hanging up the call.
   *
   * We only do this if there are 2 or less participants, since the logic
   * surrounding more than that is complex and best left to the server.
   */
  @action.bound
  maybeMarkCompleted() {
    const participantCount = this.participants.items.length
    if (participantCount > 2) {
      return
    }

    this.dateCompleted = this.getTime()
    this.callStatus = VideoCallStatus.Completed
  }

  /**
   * Maps the call status DTO to the application one.
   * @param statusType
   */
  private static mapCallStatus(
    statusType: VideoCallStatusType | undefined
  ): VideoCallStatus | undefined {
    switch (statusType) {
      case VideoCallStatusType.InProgress:
        return VideoCallStatus.InProgress
      case VideoCallStatusType.Completed:
        return VideoCallStatus.Completed
      case undefined:
        return undefined
      default:
        return VideoCallStatus.Unknown
    }
  }
}

export const enum VideoCallStatus {
  Unknown = 'Unknown',
  InProgress = 'InProgress',
  Completed = 'Completed',
}

const lazyNone = once(
  () =>
    new VideoCall(
      Conversation.none,
      () => Member.none,
      () => DateZero(),
      EMPTY
    )
)
